import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const KEY = "lc_cart_v1";
const PROMO_KEY = "lc_promo";
const CartContext = createContext(null);

function promoFromUrl() {
  const code = new URLSearchParams(window.location.search).get("promo");
  return code ? code.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, "").slice(0, 20) : "";
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch {
      return [];
    }
  });
  const [promo, setPromoState] = useState(() => promoFromUrl() || localStorage.getItem(PROMO_KEY) || "");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    if (promo) localStorage.setItem(PROMO_KEY, promo);
    else localStorage.removeItem(PROMO_KEY);
  }, [promo]);

  useEffect(() => {
    const fromUrl = promoFromUrl();
    if (!fromUrl) return;
    toast("Codice sconto salvato", { description: `${fromUrl} verrà applicato automaticamente al pagamento` });
    const url = new URL(window.location.href);
    url.searchParams.delete("promo");
    window.history.replaceState({}, "", url.pathname + url.search + (url.hash || "#merch"));
  }, []);

  const setPromo = useCallback((code) => setPromoState((code || "").trim().toUpperCase()), []);

  const add = useCallback((product, size) => {
    setItems((list) => {
      const idx = list.findIndex((i) => i.product_id === product.id && i.size === size);
      if (idx >= 0) return list.map((i, k) => (k === idx ? { ...i, quantity: Math.min(20, i.quantity + 1) } : i));
      return [...list, { product_id: product.id, name: product.name, size, price_cents: product.price_cents, image: product.image, quantity: 1 }];
    });
    setOpen(true);
  }, []);

  const setQty = useCallback((product_id, size, quantity) => {
    setItems((list) =>
      quantity <= 0
        ? list.filter((i) => !(i.product_id === product_id && i.size === size))
        : list.map((i) => (i.product_id === product_id && i.size === size ? { ...i, quantity: Math.min(20, quantity) } : i))
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const value = useMemo(() => {
    const count = items.reduce((n, i) => n + i.quantity, 0);
    const subtotal = items.reduce((n, i) => n + i.quantity * i.price_cents, 0);
    return { items, count, subtotal, add, setQty, clear, open, setOpen, promo, setPromo };
  }, [items, add, setQty, clear, open, promo, setPromo]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
export const euro = (cents) => `${(cents / 100).toFixed(2).replace(".", ",")} €`;
